<template>
  <b-offcanvas
    id="help-panel"
    v-model="show"
    placement="end"
    title="User Guide"
    width="720px"
    backdrop
    @hide="show = false"
  >
    <b-tabs v-model="activeTab" pills class="help-tabs">
      <b-tab v-for="tab in tabs" :key="tab.id" :title="tab.title">
        <div class="help-content" v-html="tab.html"></div>
      </b-tab>
    </b-tabs>
  </b-offcanvas>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { marked } from 'marked';
import DOMPurify from 'dompurify';

import overviewMd from '../../docs/user/overview.md?raw';
import patientDataMd from '../../docs/user/patient-data.md?raw';
import doseHistoryMd from '../../docs/user/dose-history.md?raw';
import resultsMd from '../../docs/user/results.md?raw';
import targetsMd from '../../docs/user/targets.md?raw';
import glossaryMd from '../../docs/user/glossary.md?raw';

defineOptions({ name: 'HelpPanel' });

const show = defineModel<boolean>({ default: false });
const activeTab = defineModel<number>('tab', { default: 0 });

function renderMd(raw: string): string {
  return DOMPurify.sanitize(marked.parse(raw) as string);
}

const tabs = computed(() => [
  { id: 'overview', title: 'Overview', html: renderMd(overviewMd) },
  { id: 'patient', title: 'Patient Data', html: renderMd(patientDataMd) },
  { id: 'doses', title: 'Dose History', html: renderMd(doseHistoryMd) },
  { id: 'results', title: 'Results', html: renderMd(resultsMd) },
  { id: 'targets', title: 'Targets', html: renderMd(targetsMd) },
  { id: 'glossary', title: 'Glossary', html: renderMd(glossaryMd) },
]);
</script>

<style scoped>
.help-tabs {
  margin-top: 0.5rem;
}
.help-content {
  padding: 1rem 0.5rem;
  font-size: 0.9rem;
  line-height: 1.6;
}
.help-content :deep(h1) {
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
}
.help-content :deep(h2) {
  font-size: 1rem;
  font-weight: 600;
  margin-top: 1.25rem;
  margin-bottom: 0.5rem;
}
.help-content :deep(h3) {
  font-size: 0.95rem;
  font-weight: 600;
  margin-top: 1rem;
  margin-bottom: 0.4rem;
}
.help-content :deep(table) {
  width: 100%;
  border-collapse: collapse;
  margin: 0.75rem 0;
  font-size: 0.85rem;
}
.help-content :deep(th),
.help-content :deep(td) {
  border: 1px solid #dee2e6;
  padding: 0.4rem 0.6rem;
  text-align: left;
}
.help-content :deep(th) {
  background: #f8f9fa;
  font-weight: 600;
}
.help-content :deep(ul),
.help-content :deep(ol) {
  padding-left: 1.4rem;
  margin-bottom: 0.75rem;
}
.help-content :deep(code) {
  background: #f1f3f5;
  padding: 0.1rem 0.3rem;
  border-radius: 3px;
  font-size: 0.85em;
}
.help-content :deep(strong) {
  font-weight: 600;
}
</style>
